//import { CdkDragDrop, CdkDragStart } from '@angular/cdk/drag-drop/typings/drag-events';
//import { DragRef } from '@angular/cdk/drag-drop/typings/drag-ref';
import { CdkDragDrop, CdkDragStart, moveItemInArray, DragRef } from '@angular/cdk/drag-drop';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';

@Component({
  selector: 'app-multi-drag-drop',
  templateUrl: './multi-drag-drop.component.html',
  styleUrls: ['./multi-drag-drop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiDragDropComponent implements OnInit, AfterViewInit, OnChanges  {
  @Input() items: any[];
  @Input() dragZone:string;
  @Input() multiple:Boolean = true;
  @Input() searchText:string = "";
  @Input() searchKey:string = "";
  // If true, the component behaves in single-selection mode (Product Detail Page)
  @Input() isPDP: boolean = false;

  @Output() itemsRemoved = new EventEmitter<any[]>();
  @Output() itemsAdded = new EventEmitter<any[]>();
  @Output() itemsUpdated = new EventEmitter<any[]>();
  @Output() selectionChanged = new EventEmitter<any[]>();
  @ContentChild(TemplateRef, { static: false }) templateRef;

  public dragging: DragRef = null;
  public selections: number[] = [];
  public filteredItems: any[] = [];
  private currentSelectionSpan: number[] = [];
  private lastSingleSelection: number;

  constructor(private eRef: ElementRef, private cdRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.filteredItems = this.items;
  }
    
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] || changes['searchText']) {
      this.filterItems();
    }
  }

  filterItems(): void {
    if (this.searchText && this.searchKey) {
      // On filtre le tableau `items`
      this.filteredItems = this.items.filter(item =>
        item && item[this.searchKey] &&
        item[this.searchKey].toLowerCase().includes(this.searchText.toLowerCase())
      );
    } else {
      this.filteredItems = this.items;
    }
    this.cdRef.detectChanges();
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  dragStarted(ev: CdkDragStart, item: any): void {
    const index = this.items.indexOf(item);
    this.dragging = ev.source._dragRef;
    const indices = this.selections.length ? this.selections : [index];
    ev.source.data = {
      indices,
      values: indices.map(i => this.items[i]),
      source: this,
    };
    this.cdRef.detectChanges();
  }

  dragEnded(): void {
    this.dragging = null;
    this.cdRef.detectChanges();
  }

  dropped(ev: CdkDragDrop<any>): void {
    if (!ev.isPointerOverContainer || !ev.item.data.source) {
      return;
    }
    const data = ev.item.data;

    if (data.source === this) {
      if (ev.previousContainer === ev.container) {
        // Reordering within the same list
        // This is complex with a filtered view, for now we can notify parent
        this.itemsUpdated.emit(this.items);
      } else {
        // Moving between lists, remove from source
        const removed = data.indices.map(i => this.items[i]);
        this.itemsRemoved.emit(removed);
      }
    }
    this.dragging = null;
    setTimeout(() => this.clearSelection());
  }
    
  droppedIntoList(ev: CdkDragDrop<any>): void {
    if (!ev.isPointerOverContainer || !ev.item.data || !ev.item.data.source) {
      return;
    }

    // --- Cas 1 : L'élément est réorganisé DANS LA MÊME LISTE ---
    if (ev.previousContainer === ev.container) {
      // On utilise la fonction d'Angular CDK pour réorganiser l'élément dans le tableau 'items'.
      moveItemInArray(this.items, ev.previousIndex, ev.currentIndex);

      // On notifie le parent que l'ordre de la liste a été mis à jour.
      this.itemsUpdated.emit(this.items);
    }
    // --- Cas 2 : L'élément est déplacé VERS UNE AUTRE LISTE (votre logique existante) ---
    else {
      const data = ev.item.data;
      if (this.isPDP && data.values.length > 0) {
        // There is a constraint of single selection (e.g. Product Detail Page)
        // If there is already an item selected, move it back to the source list
        if (this.items.length) {
          const previousItem = this.items[0];
          // Make sure the previous item is not already present in the source list
          if (!data.source.items.some((x: any) => x.id === previousItem.id)) {
            data.source.items.push(previousItem);
            // Notify the source list that items were added back
            data.source.itemsAdded.emit([previousItem]);
            data.source.itemsUpdated.emit(data.source.items);
          }
        }
        // Replace the current list with the newly dropped item(s) (take the first one only)
        this.items = [data.values[0]];
        this.itemsAdded.emit([data.values[0]]);
        this.itemsUpdated.emit(this.items);
      } else {
        const newItems = data.values.filter(newItem => !this.items.some(existingItem => existingItem.id === newItem.id));
        this.items.splice(ev.currentIndex, 0, ...newItems);
        this.itemsAdded.emit(newItems);
        this.itemsUpdated.emit(this.items);
      }
    }

    this.filterItems();
    this.cdRef.detectChanges();
    //console.log('Items after change:', this.items);
  }

  isSelected(item: any): boolean {
    const index = this.items.indexOf(item);
    return this.selections.indexOf(index) >= 0;
  }

  select(event: MouseEvent, item: any): void {
    const index = this.items.indexOf(item);
    if(this.multiple) {
      // Your existing logic for multi-selection
      // This part is complex and may need further adjustments based on desired UX
      const alreadySelected = this.selections.includes(index);
      if (event.ctrlKey || event.metaKey) {
        if(alreadySelected) {
          this.selections = this.selections.filter(s => s !== index);
        } else {
          this.selections.push(index);
        }
        this.lastSingleSelection = index;
      } else if (event.shiftKey && this.lastSingleSelection !== null) {
        // Simplified shift select for example
        const start = Math.min(this.lastSingleSelection, index);
        const end = Math.max(this.lastSingleSelection, index);
        this.selections = this.items.map((_, i) => i).slice(start, end + 1);
      } else {
          if (this.selections.length === 1 && alreadySelected) {
              this.clearSelection();
          } else {
            this.selections = [index];
            this.lastSingleSelection = index;
          }
      }
    } else { // Single selection mode
        if (this.selections.includes(index)) {
            this.clearSelection();
        } else {
            this.selections = [index];
            this.lastSingleSelection = index;
        }
    }
    this.selectionChanged.emit(this.selections.map(i => this.items[i]));
    this.cdRef.detectChanges();
  }

  clearSelection(): void {
    if (this.selections.length) {
      this.selections = [];
      this.lastSingleSelection = null;
      this.selectionChanged.emit([]);
      this.cdRef.detectChanges();
    }
  }

  selectAll() {
    if (this.selections.length !== this.items.length) {
      this.selections = this.items.map((item, i) => i);
      this.currentSelectionSpan = [];
      this.lastSingleSelection = null;
      this.selectionChanged.emit(this.items);
      this.cdRef.detectChanges();
    }
  }

  // handles "ctrl/command + a" to select all
 /* @HostListener('document:keydown', ['$event'])
  private handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'a' &&
      (event.ctrlKey || event.metaKey) &&
      this.selections.length &&
      document.activeElement.nodeName !== 'INPUT'
    ) {
      event.preventDefault();
      this.selectAll();
    } else if (event.key === 'Escape' && this.dragging) {
      this.dragging.reset();
      document.dispatchEvent(new Event('mouseup'));
    }
  }

  @HostListener('document:click', ['$event'])
  private clickout(event) {
    if (this.selections.length && !this.eRef.nativeElement.contains(event.target)) {
      this.clearSelection();
    }
  }*/
}
